import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { useUpdateUserMutation, useGetUserQuery } from "store";
import {
    Box,
    Spinner,
    Center,
    SimpleGrid,
    Card,
    CardBody,
    CardHeader,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    Checkbox,
    Button,
    Heading,
    Text,
} from "@chakra-ui/react";

const UpdateUser = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data: user, isFetching, error } = useGetUserQuery(id);
    const [ updateUser, { isLoading, isError, isSuccess }] = useUpdateUserMutation();
    const { register, handleSubmit, formState: {errors}, reset} = useForm({defaultValues: user});
    
    useEffect(() => {
        reset(user);
    }, [user, reset]);

    const onSubmit = async (data) => {
        try {
            await updateUser({ ...data, id });
            navigate(`/user/${id}/details`);
        } catch (error) {
            console.error("Failed to update user:", error);
        }
    };

    if(isFetching) {
        return (
            <Center>
                <Spinner size="xl" />
            </Center>
        );
    }

    if(error) {
        return <Text>Error loading user data</Text>;
    }

    return (
        <>
        <Box mt={20} p={4}>
            <Card boxShadow="md" borderRadius="2xl" overflow="hidden" p={4} border="1px solid" borderColor="gray.300">
                <CardHeader borderBottom="1px solid" borderColor="blue.200">
                    <Box textAlign="center" mb={4}>
                        <Text fontSize="2xl" fontWeight="bold">
                            Update User Form
                        </Text>
                    </Box>
                </CardHeader>

                <CardBody>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        {/* Basic Info */}
                        <Heading as="h5" size="lg" my={3}>
                            Data
                        </Heading>

                        {/* Username */}
                        <SimpleGrid columns={{ base: 1, md: 1}} mb={6}>
                            <Box>
                                <FormControl isInvalid={errors.username}>
                                    <FormLabel>Username</FormLabel>
                                    <Input type="text" {...register("username")} />
                                    {errors.username && (
                                        <FormErrorMessage>
                                            Username is required
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            </Box>
                        </SimpleGrid>

                        {/* Name */}
                        <SimpleGrid columns={{ base: 1, md: 1}} mb={6}>
                            <Box>
                                <FormControl isInvalid={errors.first_name}>
                                    <FormLabel>Name</FormLabel>
                                    <Input type="text" {...register("first_name")} />
                                </FormControl>
                            </Box>
                        </SimpleGrid>

                        {/* Surname */}
                        <SimpleGrid columns={{ base: 1, md: 1}} mb={6}>
                            <Box>
                                <FormControl isInvalid={errors.last_name}>
                                    <FormLabel>Surname</FormLabel>
                                    <Input type="text" {...register("last_name")} />
                                </FormControl>
                            </Box>
                        </SimpleGrid>

                        <hr className="mb-2" />

                        {/* Roles */}
                        <Heading as="h5" size="lg" my={3}>
                            Roles
                        </Heading>

                        {/* Is Admin */}
                        <SimpleGrid columns={{ base: 1, md: 3}} spacing={4} mb={6}>
                            <Box>
                                <FormControl isInvalid={errors.last_name}>
                                    <FormLabel>Admin</FormLabel>
                                    <Checkbox type="checkbox" {...register("is_hospital_admin")} label="Yes" />
                                </FormControl>
                            </Box>
                        </SimpleGrid>

                        {/* Is Receptionist */}
                        <SimpleGrid columns={{ base: 1, md: 3}} spacing={4} mb={6}>
                            <Box>
                                <FormControl isInvalid={errors.is_receptionist}>
                                    <FormLabel>Receptionist</FormLabel>
                                    <Checkbox type="checkbox" {...register("is_receptionist")} label="Yes" />
                                </FormControl>
                            </Box>
                        </SimpleGrid>

                        <SimpleGrid>
                            <Box>
                                <Button
                                    fontSize="sm"
                                    variant="outline"
                                    fontWeight="500"
                                    type="submit"
                                    w="20%"
                                    h="50"
                                    mb="24px"
                                    isLoading={isLoading}
                                    loadingText="Loading..."
                                    spinner={<Spinner />}
                                >
                                    Update
                                </Button>
                                {isError && (
                                    <Text color="red.500">
                                        Failed to update user
                                    </Text>
                                )}
                                {isSuccess && (
                                    <Text color="green.500">
                                        User updated successfully
                                    </Text>
                                )}
                            </Box>
                        </SimpleGrid>
                    </form>
                </CardBody>
            </Card>
        </Box>
        </>
    );
}

export default UpdateUser;